/*
 * Variant picker — finish-group runtime sync.
 *
 * Two responsibilities, both bound to the same `change` event so they stay
 * in lockstep with the user's selection:
 *
 * 1. Visual: mirror the currently-selected Color value into the active
 *    group's label (so the user sees "Matte: Black Stripe" or "Glossy:
 *    Black Stripe").
 *
 * 2. Cart: keep the hidden `properties[Finish]` input in sync with whichever
 *    group contains the active swatch — so an Add-to-Cart click between the
 *    swatch click and the variant-picker's section refetch still submits the
 *    correct finish label.
 *
 * Markup contract:
 *   <variant-picker>
 *     <fieldset class="option-selector" data-option="Color">
 *       <div class="option-selector__finish-group" data-finish="matte">
 *         <div class="opt-finish-label">
 *           Matte
 *           <span class="opt-finish-label__colon">:</span>
 *           <span class="opt-finish-label__value js-finish-value">...</span>
 *         </div>
 *         ...swatch radios...
 *       </div>
 *       <div class="option-selector__finish-group" data-finish="glossy">...</div>
 *     </fieldset>
 *   </variant-picker>
 *
 *   <form action="/cart/add" id="..." class="js-product-form">
 *     <input type="hidden" name="properties[Finish]"
 *            class="js-finish-property"
 *            data-finish-matte-label="Matte"
 *            data-finish-glossy-label="Glossy"
 *            value="Matte">
 *   </form>
 *
 * Both the .js-finish-value spans AND the hidden input get the correct
 * server-rendered text on every fresh render (see snippets/variant-picker.liquid
 * + snippets/finish-property-input.liquid). The JS only handles the
 * ~200ms window between a click and the variant-picker's section fetch
 * completing.
 *
 * Idempotent: registered once via the window.__variantPickerFinishGroupsInit
 * guard.
 */

(() => {
  if (window.__variantPickerFinishGroupsInit) return;
  window.__variantPickerFinishGroupsInit = true;

  /**
   * Update the .js-finish-value spans inside one finish-grouped fieldset.
   * Active group (containing the currently checked radio) gets the value
   * text; other groups are cleared.
   */
  function updateLabelSpans(fieldset) {
    const groups = fieldset.querySelectorAll('.option-selector__finish-group');
    if (!groups.length) return;
    groups.forEach((group) => {
      const valueEl = group.querySelector('.js-finish-value');
      if (!valueEl) return;
      const checked = group.querySelector('input[type="radio"]:checked');
      valueEl.textContent = checked ? checked.value : '';
    });
  }

  /**
   * Update the hidden properties[Finish] inputs on the page (one per form on
   * the section — there can be more than one when quick-buy is in use).
   *
   * Reads the active group's `data-finish` attribute and maps it to the
   * admin-defined label stored on the input itself (data-finish-matte-label
   * / data-finish-glossy-label).
   */
  function updateFinishPropertyInputs(fieldset) {
    const activeGroup = fieldset.querySelector(
      '.option-selector__finish-group:has(input[type="radio"]:checked)'
    );
    if (!activeGroup) return;
    const finish = activeGroup.dataset.finish; // 'matte' or 'glossy'

    // Find every hidden Finish property input on the page; update each one.
    // Inputs are scoped to the same product page (typically one quick-buy
    // form per product). Using document scope keeps quick-buy in sync too.
    document.querySelectorAll('input.js-finish-property').forEach((input) => {
      const matteLabel = input.dataset.finishMatteLabel || 'Matte';
      const glossyLabel = input.dataset.finishGlossyLabel || 'Glossy';
      const newValue = finish === 'glossy' ? glossyLabel : matteLabel;
      if (input.value !== newValue) input.value = newValue;
    });
  }

  /**
   * Initial population for both concerns. Script is deferred so the DOM is
   * already parsed; the spans were also server-rendered, so this is mostly
   * insurance for edge cases (e.g. the radio set differs from what Liquid
   * had at render time, which shouldn't happen but is cheap to guard).
   */
  function initAll() {
    document.querySelectorAll(
      'fieldset.option-selector:has(.option-selector__finish-group)'
    ).forEach((fieldset) => {
      updateLabelSpans(fieldset);
      updateFinishPropertyInputs(fieldset);
    });
  }

  // On every Color radio change inside a finish-grouped fieldset, update
  // both the visual label and the cart-property input.
  document.addEventListener('change', (evt) => {
    const target = evt.target;
    if (!target || target.type !== 'radio') return;
    const fieldset = target.closest('fieldset.option-selector');
    if (!fieldset) return;
    if (!fieldset.querySelector('.option-selector__finish-group')) return;
    updateLabelSpans(fieldset);
    updateFinishPropertyInputs(fieldset);
  });

  initAll();
})();
